import { useMemo, useState } from 'react';
import { ISSUE_KINDS, ISSUE_STATUSES, PRIORITIES } from '@shared/types';
import type { Attachment, Issue, IssueKind, IssueStatus, Priority } from '@shared/types';
import { api, type DirectoryEntry } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { relativeTime } from '../lib/format';
import { AttachmentGrid, AttachmentUploader } from '../components/Attachments';
import { NoteThread } from '../components/NoteThread';
import {
  EmptyState,
  Field,
  IssueStatusTag,
  LoadingPage,
  PriorityTag,
  Tag,
} from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const OPEN_STATUSES: IssueStatus[] = ['Open', 'Acknowledged', 'In Progress'];

export function RequestsPage() {
  const { user, isBoard } = useAuth();
  const { notify, fail } = useToast();
  const [filter, setFilter] = useState<'Needs action' | 'All' | IssueStatus>(
    isBoard ? 'Needs action' : 'All',
  );
  const [showForm, setShowForm] = useState(false);

  const state = useAsync(async () => (await api.issues()).issues, [user?.id]);
  const directory = useAsync(async () => (isBoard ? (await api.directory()).users : []), [isBoard]);

  const issues = state.data ?? [];
  const visible = useMemo(() => {
    if (filter === 'All') return issues;
    if (filter === 'Needs action') {
      return issues.filter((issue) => OPEN_STATUSES.includes(issue.status));
    }
    return issues.filter((issue) => issue.status === filter);
  }, [issues, filter]);

  function replace(updated: Issue) {
    state.setData(issues.map((issue) => (issue.id === updated.id ? updated : issue)));
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading requests…" />;

  return (
    <div>
      <PageHeader
        title="Complaints & Assistance"
        lede={
          isBoard
            ? 'Everything residents have reported, newest and most urgent first. Assign an owner, reply, and close the loop.'
            : 'Report something broken, raise a concern, or ask the board for help. You can attach photos straight from your phone.'
        }
      />

      <div className="row" style={{ marginBottom: 16 }}>
        <button type="button" className="btn" onClick={() => setShowForm((open) => !open)}>
          {showForm ? 'Close form' : 'New request'}
        </button>
      </div>

      {showForm && (
        <IssueForm
          onCreated={(issue) => {
            state.setData([issue, ...issues]);
            setShowForm(false);
            notify('Sent to the board.', 'success');
          }}
          onError={fail}
        />
      )}

      <div className="filters" role="tablist" aria-label="Filter requests">
        {(['Needs action', 'All', ...ISSUE_STATUSES] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={filter === option}
            className={filter === option ? 'on' : ''}
            onClick={() => setFilter(option)}
          >
            {option}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={
            filter === 'All'
              ? 'Nothing here yet.'
              : filter === 'Needs action'
                ? 'Nothing needs action.'
                : `No ${filter.toLowerCase()} requests.`
          }
          hint={isBoard ? 'Requests from residents land here.' : 'Something not right? Let the board know above.'}
        />
      ) : (
        visible.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            directory={directory.data ?? []}
            onChange={replace}
          />
        ))
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- form */

function IssueForm({
  onCreated,
  onError,
}: {
  onCreated: (issue: Issue) => void;
  onError: (error: unknown, fallback?: string) => void;
}) {
  const [form, setForm] = useState({
    kind: 'Complaint' as IssueKind,
    title: '',
    description: '',
    location: '',
    priority: 'Normal' as Priority,
    anonymous: false,
  });
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { issue } = await api.issueCreate({
        ...form,
        location: form.location || undefined,
        attachmentIds: files.map((f) => f.id),
      });
      onCreated(issue);
      setForm({
        kind: 'Complaint',
        title: '',
        description: '',
        location: '',
        priority: 'Normal',
        anonymous: false,
      });
      setFiles([]);
    } catch (error) {
      onError(error, 'Your request was not sent.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>What can we help with?</h3>
      <form onSubmit={submit}>
        <div className="grid two">
          <Field label="Type">
            <select
              value={form.kind}
              onChange={(event) => setForm({ ...form, kind: event.target.value as IssueKind })}
            >
              {ISSUE_KINDS.map((kind) => (
                <option key={kind}>{kind}</option>
              ))}
            </select>
          </Field>
          <Field label="Urgency">
            <select
              value={form.priority}
              onChange={(event) => setForm({ ...form, priority: event.target.value as Priority })}
            >
              {PRIORITIES.map((priority) => (
                <option key={priority}>{priority}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Subject">
          <input
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            placeholder="Streetlight out on Oak Court"
            required
            maxLength={160}
          />
        </Field>

        <Field label="Details">
          <textarea
            rows={4}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            placeholder="When did it start, where exactly, and what have you already tried?"
            required
          />
        </Field>

        <Field label="Location" hint="Street, common area, or your unit.">
          <input
            value={form.location}
            onChange={(event) => setForm({ ...form, location: event.target.value })}
          />
        </Field>

        <span className="label-text">Photos</span>
        <AttachmentUploader value={files} onChange={setFiles} />

        <label className="check">
          <input
            type="checkbox"
            checked={form.anonymous}
            onChange={(event) => setForm({ ...form, anonymous: event.target.checked })}
          />
          <span>
            Submit anonymously — the board sees the report but not your name. You can still follow it
            here.
          </span>
        </label>

        <div className="btn-row">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Sending…' : 'Send to the board'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------------------------------------------------- card */

function IssueCard({
  issue,
  directory,
  onChange,
}: {
  issue: Issue;
  directory: DirectoryEntry[];
  onChange: (issue: Issue) => void;
}) {
  const { isBoard } = useAuth();
  const { notify, fail } = useToast();
  const [expanded, setExpanded] = useState(issue.notes.length > 0);
  const [busy, setBusy] = useState(false);

  async function update(patch: Record<string, unknown>) {
    setBusy(true);
    try {
      const { issue: updated } = await api.issueUpdate(issue.id, patch);
      onChange(updated);
      notify('Updated.', 'success');
    } catch (error) {
      fail(error, 'That change was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function assign(userId: string) {
    setBusy(true);
    try {
      const { issue: updated } = await api.issueAssign(issue.id, userId || null);
      onChange(updated);
    } catch (error) {
      fail(error, 'The assignment was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{issue.title}</h3>
        <div className="tag-row">
          <Tag>{issue.kind}</Tag>
          <PriorityTag priority={issue.priority} />
          <IssueStatusTag status={issue.status} />
        </div>
      </div>

      <div className="mut">
        {issue.reference} · {issue.authorName}
        {issue.authorUnit ? ` · ${issue.authorUnit}` : ''} · {relativeTime(issue.createdAt)}
        {issue.location ? ` · ${issue.location}` : ''}
        {issue.assignedToName ? ` · assigned to ${issue.assignedToName}` : ''}
      </div>

      <p className="pre-wrap">{issue.description}</p>
      <AttachmentGrid items={issue.attachments} />

      {isBoard && (
        <div className="grid two" style={{ marginTop: 12 }}>
          <Field label="Status">
            <select
              value={issue.status}
              disabled={busy}
              onChange={(event) => void update({ status: event.target.value })}
            >
              {ISSUE_STATUSES.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select
              value={issue.priority}
              disabled={busy}
              onChange={(event) => void update({ priority: event.target.value })}
            >
              {PRIORITIES.map((priority) => (
                <option key={priority}>{priority}</option>
              ))}
            </select>
          </Field>
          <Field label="Assigned to">
            <select
              value={issue.assignedTo ?? ''}
              disabled={busy}
              onChange={(event) => void assign(event.target.value)}
            >
              <option value="">Unassigned</option>
              {directory
                .filter((person) => person.roles.includes('board') || person.roles.includes('admin'))
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
      )}

      <div className="btn-row">
        <button type="button" className="btn s tiny" onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Hide replies' : `Replies${issue.notes.length > 0 ? ` (${issue.notes.length})` : ''}`}
        </button>
      </div>

      {expanded && (
        <NoteThread
          notes={issue.notes}
          allowInternal={isBoard}
          placeholder={isBoard ? 'Reply to the resident…' : 'Add more detail…'}
          onSubmit={async (body, internal, attachmentIds) => {
            const { issue: updated } = await api.issueAddNote(issue.id, body, internal, attachmentIds);
            onChange(updated);
          }}
        />
      )}
    </div>
  );
}
